import React, { useEffect, useRef } from 'react';
import type * as MapLibre from 'maplibre-gl';
import {
  loadEngine, STYLE_URLS, cartoTransformRequest, creditElement, tokenColor,
  ENGINE_MAX_ZOOM, ENGINE_MIN_ZOOM,
} from '../../utils/mapUtils';
import { toEngineZoom } from '../explore/viewport';
import './WorldMap.css';

export interface WorldMapDestination {
  name: string;
  lat: number;
  lng: number;
  visits: number;
  status?: 'visited' | 'upcoming' | 'dreaming';
}

interface WorldMapProps {
  destinations: WorldMapDestination[];
}

/** The opening view: centred on 20°N 0°E at Leaflet's zoom 2 — the same
 *  world, in the engine's scale. */
export const WORLD_CENTRE: [number, number] = [20, 0];
export const WORLD_ZOOM = toEngineZoom(2);

const SOURCE = 'world-map-destinations';
const LAYER = 'world-map-destinations-circles';

const tooltipText = (dest: WorldMapDestination): string => {
  const label = dest.status === 'dreaming' ? 'Bucket list'
    : dest.status === 'upcoming' ? 'Upcoming'
      : dest.visits > 1 ? `${dest.visits}x` : '';
  return `${dest.name}${label ? ` (${label})` : ''}`;
};

/** One circle feature per destination. The engine draws these, and engine
 *  paint cannot read a CSS variable, so colours are resolved through
 *  `tokenColor`. Ink on grey: visited = ink, upcoming = slate, dreaming =
 *  faint slate. */
export const destinationFeatures = (destinations: WorldMapDestination[]): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: destinations.map((dest) => ({
    type: 'Feature',
    properties: {
      tooltip: tooltipText(dest),
      color: dest.status === 'upcoming' ? tokenColor('--text-secondary')
        : dest.status === 'dreaming' ? tokenColor('--text-tertiary')
          : tokenColor('--text-primary'),
      opacity: dest.status === 'dreaming' ? 0.5 : 0.8,
      radius: dest.status === 'dreaming' ? 5 : 6 + Math.min(dest.visits, 5) * 2,
    },
    geometry: { type: 'Point', coordinates: [dest.lng, dest.lat] },
  })),
});

const WorldMap: React.FC<WorldMapProps> = ({ destinations }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibre.Map | null>(null);
  const loadedRef = useRef(false);
  const dataRef = useRef(destinations);
  dataRef.current = destinations;

  const hasDestinations = destinations.length > 0;

  useEffect(() => {
    if (!hasDestinations) return undefined;
    let alive = true;
    let map: MapLibre.Map | null = null;
    let credit: HTMLElement | null = null;
    loadEngine().then((ns) => {
      const container = containerRef.current;
      if (!alive || !container) return;
      // Without WebGL this throws; the trailing catch below takes it.
      const drawn = new ns.Map({
        container,
        style: STYLE_URLS.noLabels,
        transformRequest: cartoTransformRequest(),
        attributionControl: false,
        scrollZoom: false,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        center: [WORLD_CENTRE[1], WORLD_CENTRE[0]],
        zoom: WORLD_ZOOM,
        maxZoom: ENGINE_MAX_ZOOM,
        minZoom: ENGINE_MIN_ZOOM,
      });
      map = drawn;
      // Leaflet's default +/- stays (wheel zoom stays off); rotation and pitch
      // stay off (plan §1 non-goal, and the bearing-0 camera arithmetic of D9).
      drawn.addControl(new ns.NavigationControl({ showCompass: false, showZoom: true }), 'top-left');
      drawn.touchZoomRotate.disableRotation();
      drawn.keyboard.disableRotation();
      mapRef.current = drawn;
      credit = container.appendChild(creditElement());
      const popup = new ns.Popup({ closeButton: false, closeOnClick: false });

      drawn.on('load', () => {
        if (!alive) return;
        loadedRef.current = true;
        drawn.addSource(SOURCE, { type: 'geojson', data: destinationFeatures(dataRef.current) });
        drawn.addLayer({
          id: LAYER,
          type: 'circle',
          source: SOURCE,
          paint: {
            'circle-color': ['get', 'color'],
            'circle-opacity': ['get', 'opacity'],
            'circle-radius': ['get', 'radius'],
            'circle-stroke-color': tokenColor('--bg-primary'),
            'circle-stroke-width': 2,
          },
        });
      });

      // The tooltip: a popup on hover, its text set as a node, never markup.
      drawn.on('mouseenter', LAYER, (e) => {
        const feature = e.features?.[0];
        if (!feature || feature.geometry.type !== 'Point') return;
        const [lng, lat] = feature.geometry.coordinates;
        popup
          .setLngLat([lng, lat])
          .setDOMContent(document.createTextNode(String(feature.properties?.tooltip ?? '')))
          .addTo(drawn);
      });
      drawn.on('mouseleave', LAYER, () => { popup.remove(); });
    }).catch(() => {
      // Loading or initialisation failed: the engine chunk did not load, or
      // the map could not start (no WebGL, so the constructor threw). The pane
      // stays empty rather than taking the page down with it.
    });
    return () => {
      alive = false;
      loadedRef.current = false;
      mapRef.current = null;
      if (map) map.remove();
      if (credit) credit.remove();
    };
  }, [hasDestinations]);

  // New destinations after the style loaded replace the source's data.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    const source = map.getSource(SOURCE) as MapLibre.GeoJSONSource | undefined;
    if (source) source.setData(destinationFeatures(destinations));
  }, [destinations]);

  if (!hasDestinations) return null;

  return (
    <div className="world-map" data-testid="world-map">
      <div ref={containerRef} className="world-map-container" data-testid="world-map-canvas" />
    </div>
  );
};

export default WorldMap;
